import React from 'react';
import { Phone, AlertCircle, Clock, CheckCircle2 } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

const Urgences = () => {
  const urgencySituations = [
    {
      title: 'Fuite d\'eau importante',
      description: 'Inondation, rupture de canalisation, fuite au niveau du compteur',
      action: 'Coupez l\'eau au compteur principal et appelez-nous immédiatement'
    },
    {
      title: 'WC bouchés',
      description: 'Toilettes débordantes, canalisations complètement obstruées',
      action: 'N\'utilisez plus les toilettes et contactez notre service d\'urgence'
    },
    {
      title: 'Chauffe-eau en panne',
      description: 'Plus d\'eau chaude, fuite du ballon, disjoncteur qui saute',
      action: 'Coupez l\'alimentation électrique et appelez-nous pour une intervention rapide'
    },
    {
      title: 'Canalisation éclatée',
      description: 'Gel, surpression, vieillissement des tuyaux',
      action: 'Fermez le robinet d\'arrêt et appelez notre équipe d\'urgence 24/7'
    },
    {
      title: 'Refoulement d\'eaux usées',
      description: 'Évacuation bloquée, eaux qui remontent',
      action: 'Évitez d\'utiliser l\'eau et contactez-nous pour un débouchage d\'urgence'
    },
    {
      title: 'Robinetterie cassée',
      description: 'Robinet qui fuit en continu, poignée cassée, impossible de fermer',
      action: 'Coupez l\'arrivée d\'eau et appelez pour une réparation immédiate'
    }
  ];

  const interventionSteps = [
    { step: '1', title: 'Appel d\'urgence', description: 'Contactez-nous au 09 70 70 58 63' },
    { step: '2', title: 'Diagnostic rapide', description: 'Nous évaluons la situation par téléphone' },
    { step: '3', title: 'Intervention sous 30 min', description: 'Notre plombier se déplace immédiatement' },
    { step: '4', title: 'Réparation efficace', description: 'Résolution du problème avec matériel professionnel' }
  ];

  return (
    <>
      <SEO
        title="Urgence Plomberie 24/7 - NHM Plomberie Paris 17e"
        description="Service d'urgence plomberie disponible 24h/24 et 7j/7 à Paris 17e. Intervention rapide sous 30 minutes pour fuites, débouchage, pannes. Appelez le 09 70 70 58 63."
        keywords="urgence plomberie, plombier urgence 24/7, dépannage urgent, fuite eau urgence, plombier nuit, plombier dimanche, intervention rapide plomberie"
      />

      {/* Emergency Alert Banner */}
      <section className="bg-red-600 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <AlertCircle className="w-12 h-12 flex-shrink-0" />
              <div>
                <h1 className="text-3xl font-bold">Urgence Plomberie 24/7</h1>
                <p className="text-red-100">Intervention rapide à Paris 17e et environs</p>
              </div>
            </div>
            <Button size="lg" className="bg-white text-red-600 hover:bg-red-50 text-xl px-8 py-6 whitespace-nowrap">
              <a href="tel:0970705863" className="flex items-center gap-2">
                <Phone className="w-6 h-6" />
                09 70 70 58 63
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <Alert className="border-2 border-blue-600 bg-blue-50">
            <Clock className="h-5 w-5 text-blue-600" />
            <AlertTitle className="text-xl font-bold text-blue-900">Disponibilité 24h/24, 7j/7</AlertTitle>
            <AlertDescription className="text-blue-800 text-lg">
              Nos plombiers sont disponibles en permanence pour intervenir rapidement sur tous vos problèmes de plomberie urgents. 
              Temps d'intervention moyen: moins de 30 minutes.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <Card className="border-2 border-blue-600">
              <CardContent className="pt-6 text-center">
                <Clock className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Intervention Rapide</h3>
                <p className="text-gray-600">Sur place en moins de 30 minutes</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-600">
              <CardContent className="pt-6 text-center">
                <CheckCircle2 className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Plombiers Qualifiés</h3>
                <p className="text-gray-600">Équipe expérimentée et équipée</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-600">
              <CardContent className="pt-6 text-center">
                <Phone className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Disponible 24/7</h3>
                <p className="text-gray-600">Jour, nuit, week-end et fériés</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Emergency Situations */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Situations d\'Urgence</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {urgencySituations.map((situation, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-xl text-red-600">{situation.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{situation.description}</p>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm font-semibold text-blue-900">⚠️ Action immédiate:</p>
                    <p className="text-sm text-blue-800 mt-1">{situation.action}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Intervention Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Notre Processus d\'Intervention</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {interventionSteps.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <AlertCircle className="w-20 h-20 mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-4">Une Urgence Plomberie ?</h2>
          <p className="text-xl mb-2 text-blue-100">N'attendez pas que la situation empire !</p>
          <p className="text-2xl font-bold mb-8">Appelez maintenant pour une intervention immédiate</p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-2xl px-12 py-8">
            <a href="tel:0970705863" className="flex items-center gap-3">
              <Phone className="w-8 h-8" />
              09 70 70 58 63
            </a>
          </Button>
          <p className="mt-6 text-blue-200">Intervention sous 30 minutes - 24h/24, 7j/7</p>
        </div>
      </section>
    </>
  );
};

export default Urgences;